import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as Blockly from 'blockly/core';
import TutorialBody from '@sections/creabots/components/tutorialBody';
import { movingHand, stopMovingHand } from '@sections/creabots/features/tutorialHints';
import { startElementBlocker, stopElementBlocker } from '@sections/creabots/features/elementBlocker';
import { useBlockly } from '@modules/blockly/features/hooks';
import * as routes from '@educabot/educablocks-cosmos';
import { useSelector } from 'react-redux';
import useCreabotsTheme from '../hooks/useCreabotsTheme';
import { selectCreabots } from '../features/creabotsSlice';
import { CODE_CHECK_TYPE } from '@sections/creabots/types/kits'
import { setMLActive, setMLTrainingStep } from '@modules/blockly/features/blocklySlice';

// setDisabledReason (v13) acumula razones: hay que usar siempre la misma para togglearla.
const TUTORIAL_STEP_DISABLED_REASON = 'creabotsTutorialStep';


const Tutorial = (props) => {
  const dispatch = useDispatch();
  const onSaveProgress = props.onSaveProgress || null;
  const data = props.data || null;
  const workspace = props.workspace || null;
  const [steps, setSteps] = useState([]);
  const [hints, setHints] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalStep, setTotalStep] = useState(data?.length || 0);
  const [currentStepId, setCurrentStepId] = useState(0);
  const [totalStepId, setTotalStepId] = useState(0);
  const blocklyState = useBlockly();
  const blocklyCodeRef = useRef();
  const blocklyBuiltRef = useRef();
  const machineLearning = useRef({});
  const { isDarkMode } = useCreabotsTheme()
  const { kits } = useSelector(selectCreabots);

  useEffect(() => {
    if (machineLearning.current) {
      machineLearning.current = blocklyState?.machineLearning || {};
      refreshCategoryBlocks();
    }
  }, [blocklyState?.machineLearning]);

  useEffect(() => {
    return () => {
      stopMovingHand();
      stopElementBlocker();
      setCurrentStep(0);
      setCurrentStepId(0);
      setSteps([]);
      setHints([]);
      Blockly.WidgetDiv.hide();
      if (workspace) {
        workspace.removeChangeListener(refreshCategoryBlocks);
      }
      stopMovingHand();
      stopElementBlocker();
    }
  }, []);

  useEffect(() => {
    const handImageTutorial = document.getElementById('hand-image-tutorial');
    if (!handImageTutorial) return;
    handImageTutorial.src = `/images/creabots/${isDarkMode ? 'hand-white.png' : 'hand.png'}`;
  }, [isDarkMode]);

  useEffect(() => {
    blocklyCodeRef.current = blocklyState?.project?.code || '';
  }, [blocklyState?.project?.code]);

  useEffect(() => {
    if (blocklyState?.build?.building && (blocklyState?.build?.port !== '' || blocklyState?.build?.microbitForceDownload)) {
      blocklyBuiltRef.current = true;
      workspace.fireChangeListener('resize');
    } else {
      blocklyBuiltRef.current = false;
    }
  }, [blocklyState?.build?.building]);

  useEffect(() => {
    let totalSteps = 0;
    let prevStepId = -1;
    if (data) {
      setSteps(data.map((tutorial, index) => {
        if (prevStepId !== tutorial.step.stepId) {
          totalSteps++;
          prevStepId = tutorial.step.stepId;
        }
        if (tutorial.completed && data.length > index + 1) {
          setCurrentStep(index + 1);
          setCurrentStepId(data[index + 1].step.stepId);
        }
        return (tutorial.step) ? {
          ...tutorial.step,
          // completed: false,
          completed: (tutorial.completed),
        } : {}
      }));
      setHints(data.map((tutorial) => tutorial.hints || []));
    }
    setTotalStep(data?.length || 0);
    setTotalStepId(totalSteps);
  }, [data]);

  useEffect(() => {
    if (workspace) {
      if (steps[currentStep]) {
        if (steps[currentStep].iaStep) {
          dispatch(setMLActive(true));
          dispatch(setMLTrainingStep(steps[currentStep].iaStep));
        } else {
          dispatch(setMLActive(false));
        }
        saveCheckedBlocksWS();
        resetWorkspace();
        stopMovingHand();
        stopElementBlocker();
        setCategoryBlocks(true);
        workspace.removeChangeListener(refreshCategoryBlocks);
        workspace.addChangeListener(refreshCategoryBlocks);
        if (!steps[currentStep]?.completed) {
          movingHand(hints[currentStep], workspace);
          if (props.iaModel && hints[currentStep] && hints[currentStep].length > 0) {
            setTimeout(() => {
              startElementBlocker(hints[currentStep], workspace);
            }, 300);
          }
        }
      } else {
        stopMovingHand();
        stopElementBlocker();
      }
    }

    return () => {
      if (workspace && steps[currentStep]) {
        workspace.removeChangeListener(refreshCategoryBlocks);
      }
    }
  }, [workspace, currentStep]);

  const saveCheckedBlocksWS = () => {
    if (workspace) {
      workspace.checkedBlocksDB = workspace.getAllBlocks().filter((block) => block.isEnabled())
        .map((block) => ({ id: block.id, type: block.type }));
    }
  }

  const sanitizeCodeString = (code) => {
    // TODO: these are rules to normalize Arduino's generated code for comparison.
    let sanitizedCode = code.replace(/\n|\s/gi, '');
    // blocklyNew nombra el bitmap con el block.id saneado (alfanumérico), no solo dígitos.
    sanitizedCode = sanitizedCode.replace(/bitmap_[a-zA-Z0-9]+/gi, 'bitmapMatrix');

    return sanitizedCode;
  };

  const challengeCompleted = () => {
    if (machineLearning.current && steps[currentStep]) {
      if (steps[currentStep].completionCodeCheck && steps[currentStep].completionCodeCheck === CODE_CHECK_TYPE.OBJECT_CONDITION) {
        // console.log('===========machineLearning', steps[currentStep].completionCode(machineLearning.current), machineLearning.current);
        return typeof steps[currentStep].completionCode === 'function' ? steps[currentStep].completionCode(machineLearning.current) : false;
      }
    }

    if (blocklyCodeRef.current) {
      // console.log('==================WITHWORKSPACE', new XMLSerializer().serializeToString(window.Blockly.Xml.workspaceToDom(workspace)));
      // console.log('==================CHECKWIDGET', window.Blockly.WidgetDiv.isVisible(), window.Blockly.WidgetDiv.DIV.className);
      if (steps[currentStep].completionCodeCheck && steps[currentStep].completionCodeCheck === CODE_CHECK_TYPE.BUILT) {
        if (blocklyBuiltRef.current) {
          return true;
        }
        return false;
      } if (steps[currentStep].completionCodeCheck && steps[currentStep].completionCodeCheck === CODE_CHECK_TYPE.VARIABLE) {
        if (workspace.getVariableMap().getAllVariables().length > 0) {
          return true;
        }
        return false;
      } if (!Blockly.WidgetDiv.isVisible() || Blockly.WidgetDiv.getDiv()?.className?.includes('fieldTextInput')) {
        const code = sanitizeCodeString(blocklyCodeRef.current);
        console.log('Blockly code: ', code);
        console.log('Current step code: ', steps[currentStep].completionCode);
        // console.log('==================CODE', workspace.getAllVariables(), new RegExp(steps[currentStep].completionCode, 'i').test(code), new RegExp(steps[currentStep].completionCode, 'i'), code);
        if (
          ((!steps[currentStep].completionCodeCheck || steps[currentStep].completionCodeCheck === CODE_CHECK_TYPE.EQUAL) && steps[currentStep].completionCode && code === steps[currentStep].completionCode)
          || (steps[currentStep].completionCodeCheck === CODE_CHECK_TYPE.REGEX && new RegExp(steps[currentStep].completionCode, 'i').test(code))
        ) {
          // `workspaceToCode()` también genera código de los bloques sueltos: más de un
          // bloque raíz significa que el bloque pedido quedó sin encastrar.
          if (workspace && workspace.getTopBlocks(false).length > 1) {
            return false;
          }
          return true;
        }
      }
    }
    return false;
  }

  const scrollWorkspaceByX = (ws, x = 0, y = 0) => {
    // `scroll()` de la v13 toma el offset de pantalla directo y ya clampea al
    // contenido: no agregar clamps a mano, que cancelan el desplazamiento.
    const metrics = ws.getMetrics();
    if (metrics) {
      ws.scroll(x + 20, y + 10);
    }
  }

  const setCategoryBlocks = (move = false) => {
    if (workspace) {
      const enableOneBlock = (ws, blockIndex) => {
        if (typeof blockIndex === 'undefined') return;
        const flyoutWorkspace = ws.getToolbox()?.getFlyout()?.getWorkspace();
        if (!flyoutWorkspace) return;
        const blocks = flyoutWorkspace.getAllBlocks(false);
        if (blocks.length > 0) {
          blocks.forEach((block) => {
            if (!block.getParent()) {
              block.setDisabledReason(true, TUTORIAL_STEP_DISABLED_REASON);
            }
          });
          blocks[blockIndex]?.setDisabledReason(false, TUTORIAL_STEP_DISABLED_REASON);
        }
      }

      if (typeof steps[currentStep].blockCategory !== 'undefined') {
        // OPEN TOOLBOX CATEGORY AND DISABLE SOME BLOCKS PROGRAMMATICALLY AND MOVE INITIAL BLOCK ASIDE OF THE CATEGORY MENU
        if (move && steps[currentStep].canvasDisplacement) {
          scrollWorkspaceByX(workspace, steps[currentStep].canvasDisplacement.x, steps[currentStep].canvasDisplacement.y);
        }
        const toolbox = workspace.getToolbox();
        // `getToolboxItems()` es plana e incluye subcategorías; `blockCategory` de
        // los tutoriales cuenta solo el nivel superior, por eso el filtro.
        const items = toolbox ? toolbox.getToolboxItems().filter((item) => item.getParent() === null) : [];
        let child = items[steps[currentStep].blockCategory] || null;
        if (child) {
          if (typeof child.getChildToolboxItems === 'function' && typeof steps[currentStep].blockSubCategory !== 'undefined') {
            // En la v13, con el padre colapsado `setSelectedItem(hijo)` no hace
            // nada sin avisar: hay que expandirlo antes.
            if (typeof child.setExpanded === 'function' && !child.isExpanded()) {
              child.setExpanded(true);
            }
            child = child.getChildToolboxItems()[steps[currentStep].blockSubCategory] || child;
          }
          // Reseleccionar el item ya seleccionado cierra y reabre el flyout en la
          // v13; como esto corre en cada evento, sin la guarda parpadea.
          if (toolbox.getSelectedItem() !== child) {
            toolbox.setSelectedItem(child);
          }
          if (typeof steps[currentStep].block !== 'undefined') {
            enableOneBlock(workspace, steps[currentStep].block);
            toolbox.getFlyout()?.getWorkspace()?.scroll(0, -(steps[currentStep].block * 50));
          }
        }
      } else if (move) {
        // CLOSE ANY CATEGORY PREVIOUSLY OPENED AND MOVE INITIAL BLOCK TO ORIGINAL POSITION
        if (steps[currentStep].canvasDisplacement) {
          scrollWorkspaceByX(workspace, steps[currentStep].canvasDisplacement.x, steps[currentStep].canvasDisplacement.y);
        } else {
          scrollWorkspaceByX(workspace, 0, 0);
        }
        const toolboxRef = workspace.getToolbox();
        if (toolboxRef) {
          toolboxRef.clearSelection();
        }
      }
    }
  }

  const refreshCategoryBlocks = (event) => {
    // Los eventos de UI no deben llamar a `setCategoryBlocks`: su
    // `setSelectedItem` hace `hideChaff()` y cierra el editor de campo recién abierto.
    // El re-chequeo tras cargar a la placa (`fireChangeListener('resize')`) llega sin `type` y sí se valida.
    const isCosmeticEvent = event
      && typeof event.type === 'string'
      && (event.type === Blockly.Events.CLICK
        || event.type === Blockly.Events.SELECTED
        || event.type === Blockly.Events.BLOCK_FIELD_INTERMEDIATE_CHANGE
        || event.type === Blockly.Events.VIEWPORT_CHANGE
        || event.type === Blockly.Events.TOOLBOX_ITEM_SELECT
        || event.type === Blockly.Events.BUBBLE_OPEN
        || event.type === Blockly.Events.MARKER_MOVE
        || event.type === Blockly.Events.THEME_CHANGE);

    // Con categoría fija, un click en otra categoría vuelve a la del paso, como en el legacy.
    if (event && event.type === Blockly.Events.TOOLBOX_ITEM_SELECT) {
      // Reseleccionar la categoria hace hideChaff y cierra el desplegable que se esta abriendo.
      if (!Blockly.DropDownDiv.isVisible() && !Blockly.WidgetDiv.isVisible()) {
        setCategoryBlocks();
      }
      return;
    }

    // Pero SELECTED y BLOCK_FIELD_INTERMEDIATE_CHANGE sí rechequean la completitud:
    // en la v13 elegir un dropdown o editar un campo no emite `change`, solo estos.
    if (
      isCosmeticEvent
      && event.type !== Blockly.Events.SELECTED
      && event.type !== Blockly.Events.BLOCK_FIELD_INTERMEDIATE_CHANGE
    ) {
      return;
    }

    if (!isCosmeticEvent) {
      stopMovingHand();
      setCategoryBlocks();
    }

    if (!steps[currentStep]?.completed) {
      setTimeout(() => {
        if (challengeCompleted()) {
          if (onSaveProgress) {
            onSaveProgress(currentStep);
          }
          setSteps(steps.map((step, i) => {
            return (i === currentStep) ? {
              ...step,
              completed: true,
            } : step;
          }));
          // TODO: make an animation or hint to show step success
          workspace.removeChangeListener(refreshCategoryBlocks);
          stopMovingHand();
          stopElementBlocker();
          saveCheckedBlocksWS();
          setTimeout(onNextStep, 500);
        } else {
          movingHand(hints[currentStep], workspace);

          if (props.iaModel && hints[currentStep] && hints[currentStep].length > 0) {
            setTimeout(() => {
              startElementBlocker(hints[currentStep], workspace);
            }, 300);
          }
        }
      }, 200);
    }
  };

  const setStepByStepId = (stepId) => {
    const lastIndex = steps.reduce((acc, step, index) => {
      return step.stepId === stepId && step.completed === true ? index : acc;
    }, -1);
    if (lastIndex !== -1) {
      if (steps[lastIndex + 1] && steps[lastIndex + 1].stepId === stepId) {
        setCurrentStep(lastIndex + 1);
      } else {
        setCurrentStep(lastIndex);
      }
    } else {
      const stepIndex = steps.findIndex((step) => step.stepId === stepId);
      if (stepIndex !== -1) {
        setCurrentStep(stepIndex);
      }
    }
  }

  const resetWorkspace = () => {
    if (steps[currentStep].resetWorkspace) {
      // No recorrer `getAllBlocks()` con dispose: el padre ya se lleva a sus hijos
      // y disponerlos de nuevo rompe la v13. Se dispone una vez por cadena.
      const initial = workspace.getAllBlocks(false).find((block) => block.type === 'initial_block');
      if (initial) {
        ['Start', 'Repeat'].forEach((inputName) => {
          const child = initial.getInputTargetBlock(inputName);
          if (child) {
            child.dispose();
          }
        });
      }
      workspace.getTopBlocks(false).forEach((block) => {
        if (block.type !== 'initial_block') {
          block.dispose();
        }
      });
    }
  }

  const onNextStepId = () => {
    if (currentStepId + 1 < totalStepId) {
      if (onSaveProgress && !steps[currentStep]?.completed && !steps[currentStep]?.completionCode && !steps[currentStep]?.completionCodeCheck) {
        onSaveProgress(currentStep);
        setSteps(steps.map((step, i) => {
          return (i === currentStep) ? {
            ...step,
            completed: true,
          } : step;
        }));
      }
      setCurrentStepId(currentStepId + 1);
      setStepByStepId(currentStepId + 1);
    } else {
      // END OF TUTORIAL
      setCurrentStep(currentStep + 1);
    }
  }

  const onPrevStepId = () => {
    if (currentStepId > 0) {
      setCurrentStepId(currentStepId - 1);
      setStepByStepId(currentStepId - 1);
    }
  }

  const setStepId = (stepIndex) => {
    if (typeof steps[stepIndex].stepId !== 'undefined') {
      setCurrentStepId(steps[stepIndex].stepId);
    }
  }

  const onNextStep = () => {
    if (currentStep < totalStep - 1) {
      setCurrentStep(currentStep + 1);
      setStepId(currentStep + 1);
    }
  }

  const onPrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setStepId(currentStep - 1);
    }
  }

  const backToHome = () => {
    window.location.href = `${routes.bloquesUri}/kits/creabots/${kits.selected.urlName}`
  }

  const nextStepEnabled = () => {
    if (
      totalStepId
      && (
        (!steps[currentStep]?.completionCode && (!steps[currentStep]?.completionCodeCheck || (steps[currentStep]?.completionCodeCheck !== CODE_CHECK_TYPE.BUILT && steps[currentStep]?.completionCodeCheck !== CODE_CHECK_TYPE.VARIABLE)))
        || steps[currentStep]?.completed
      )
    ) {
      return true;
    }
    return false;
  };

  return (
    <TutorialBody
      steps={steps}
      currentStep={currentStep}
      currentStepId={currentStepId}
      totalStepId={totalStepId}
      onPrevStep={(totalStepId && currentStepId > 0) ? onPrevStepId : null}
      onNextStep={nextStepEnabled() ? onNextStepId : null}
      backToHome={backToHome}
    />
  );
};


export default Tutorial;